/**
 * useAdvancedUnlock — Checks if the user has met the requirements to unlock advanced mode.
 *
 * Requirements (all 5 must be met, OR bypass entry exists):
 * 1. At least 1 planning template exists
 * 2. At least 1 program exists
 * 3. At least 6 sessions exist
 * 4. At least 3 sessions linked to a program
 * 5. At least 1 active program
 *
 * @since Progressive Interface System v2
 */

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const ADVANCED_MODULE = "interface_advanced";
const BYPASS_STEP = "bypass";

export interface UnlockRequirement {
  id: string;
  label: string;
  met: boolean;
  current: number;
  target: number;
  actionRoute: string;
  actionLabel: string;
}

export interface AdvancedUnlockState {
  isUnlocked: boolean;
  requirements: UnlockRequirement[];
  completedCount: number;
  totalCount: number;
  loading: boolean;
  bypass: () => Promise<void>;
}

export function useAdvancedUnlock(): AdvancedUnlockState {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["advanced-unlock", user?.id],
    queryFn: async (): Promise<{ requirements: UnlockRequirement[]; hasBypass: boolean }> => {
      if (!user) return { requirements: [], hasBypass: false };

      // Check bypass
      const { count: bypassCount } = await supabase
        .from("user_module_activations")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("module_name", ADVANCED_MODULE)
        .eq("step_name", BYPASS_STEP);

      const hasBypass = (bypassCount ?? 0) >= 1;

      // Get user's program IDs
      const { data: userPrograms } = await supabase
        .from("programs" as any)
        .select("id")
        .eq("user_id", user.id);
      const programIds = userPrograms?.map((p: any) => p.id) ?? [];

      const [templates, programs, sessions, linkedSessions, intentProfile] = await Promise.all([
        supabase
          .from("planning_week_templates")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("programs" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("sessions" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        programIds.length > 0
          ? supabase
              .from("program_sessions")
              .select("id", { count: "exact", head: true })
              .in("program_id", programIds)
          : Promise.resolve({ count: 0 }),
        supabase
          .from("user_intent_profiles")
          .select("current_program_id")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);

      const templateCount = templates.count ?? 0;
      const programCount = programs.count ?? 0;
      const sessionCount = sessions.count ?? 0;
      const linkedCount = linkedSessions.count ?? 0;
      const hasLinkedProgram = !!(intentProfile.data as any)?.current_program_id;

      let hasStatusActive = false;
      if (!hasLinkedProgram) {
        const [statusCheck, weekTypesCheck] = await Promise.all([
          supabase
            .from("programs" as any)
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .eq("status", "active"),
          supabase
            .from("programs" as any)
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .not("active_week_types", "eq", "{}"),
        ]);
        hasStatusActive = ((statusCheck.count ?? 0) >= 1) || ((weekTypesCheck.count ?? 0) >= 1);
      }
      const activeCount = (hasLinkedProgram || hasStatusActive) ? 1 : 0;

      const requirements: UnlockRequirement[] = [
        {
          id: "planning-template",
          label: "Créer un planning hebdomadaire",
          met: templateCount >= 1,
          current: Math.min(templateCount, 1),
          target: 1,
          actionRoute: "/planning",
          actionLabel: "Créer un planning",
        },
        {
          id: "program-created",
          label: "Créer un programme",
          met: programCount >= 1,
          current: Math.min(programCount, 1),
          target: 1,
          actionRoute: "/programs?tab=generate",
          actionLabel: "Créer un programme",
        },
        {
          id: "sessions-created",
          label: "Créer 6 séances",
          met: sessionCount >= 6,
          current: Math.min(sessionCount, 6),
          target: 6,
          actionRoute: "/sessions?tab=create",
          actionLabel: "Créer une séance",
        },
        {
          id: "sessions-linked",
          label: "Lier 3 séances à un programme",
          met: linkedCount >= 3,
          current: Math.min(linkedCount, 3),
          target: 3,
          actionRoute: "/programs",
          actionLabel: "Ouvrir mes programmes",
        },
        {
          id: "program-active",
          label: "Lier un programme au planning",
          met: activeCount >= 1,
          current: Math.min(activeCount, 1),
          target: 1,
          actionRoute: "/planning",
          actionLabel: "Ouvrir le planning",
        },
      ];

      return { requirements, hasBypass };
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  const requirements = data?.requirements ?? [];
  const hasBypass = data?.hasBypass ?? false;
  const completedCount = requirements.filter((r) => r.met).length;
  const hasActiveProgram = requirements.find((r) => r.id === "program-active")?.met ?? false;
  const allMet = completedCount === requirements.length && requirements.length > 0;

  const bypass = useCallback(async () => {
    if (!user) return;
    const { error } = await supabase
      .from("user_module_activations")
      .insert({
        user_id: user.id,
        module_id: `${ADVANCED_MODULE}:${BYPASS_STEP}`,
        module_name: ADVANCED_MODULE,
        step_name: BYPASS_STEP,
        activation_type: "manual",
      } as any);

    if (error && error.code !== "23505") {
      if (import.meta.env.DEV) console.error("[advanced-unlock] bypass error:", error);
      toast.error("Impossible de débloquer le mode Avancé.");
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["advanced-unlock", user.id] });
    toast.success("Mode Avancé débloqué !");
  }, [user, queryClient]);

  return {
    isUnlocked: allMet || hasActiveProgram || hasBypass,
    requirements,
    completedCount,
    totalCount: requirements.length,
    loading: isLoading,
    bypass,
  };
}
